// src/hooks/useStudyMode.ts
// Hook para gerenciar lógica dos Modos de Estudo

import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export type StudyMode = 'free' | 'focus' | 'review';

interface UseStudyModeReturn {
    mode: StudyMode;
    isFocusMode: boolean;
    isReviewMode: boolean;
    isFreeMode: boolean;
    focusTimeRemaining: number; // segundos
    focusSessionValid: boolean;
    handleExit: (callback: () => void) => void;
    confirmExit: (callback: () => void) => void;
    markFocusComplete: () => void;
    shouldShowExitWarning: () => boolean;
    addFocusTime: () => void;
}

const FOCUS_DURATION = 20 * 60; // 20 minutos em segundos

export const useStudyMode = (): UseStudyModeReturn => {
    const location = useLocation();
    // Detectar modo via query parameter
    const queryParams = new URLSearchParams(location.search);
    const modeParam = queryParams.get('mode') as StudyMode;
    const mode: StudyMode = modeParam || 'free';

    const [focusTimeRemaining, setFocusTimeRemaining] = useState(FOCUS_DURATION);
    const [focusSessionValid, setFocusSessionValid] = useState(true);
    const focusStartTime = useRef<number | null>(null);
    const timerInterval = useRef<NodeJS.Timeout | null>(null);
    const totalDurationRef = useRef<number>(FOCUS_DURATION);

    // ===== INICIALIZAR MODO FOCO =====
    useEffect(() => {
        if (mode === 'focus') {
            console.log('🔥 MODO FOCO ATIVADO');

            // Iniciar timer
            focusStartTime.current = Date.now();
            setFocusTimeRemaining(FOCUS_DURATION);
            setFocusSessionValid(true);
            totalDurationRef.current = FOCUS_DURATION;

            // Ativar fullscreen
            enterFullscreen();

            // Timer countdown
            timerInterval.current = setInterval(() => {
                if (focusStartTime.current) {
                    const elapsed = Math.floor((Date.now() - focusStartTime.current) / 1000);
                    const remaining = Math.max(0, totalDurationRef.current - elapsed);
                    setFocusTimeRemaining(remaining);
                    // NÃO para mais — continua rodando para capturar +10
                }
            }, 1000);

            // Cleanup
            return () => {
                if (timerInterval.current) {
                    clearInterval(timerInterval.current);
                }
                exitFullscreen();
            };
        }
    }, [mode]);

    // ===== FULLSCREEN =====
    const enterFullscreen = () => {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
            elem.requestFullscreen().catch(err => {
                console.warn('⚠️ Fullscreen não suportado:', err);
            });
        }
    };

    const exitFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(err => {
                console.warn('⚠️ Erro ao sair do fullscreen:', err);
            });
        }
    };

    // ===== VERIFICAR SE DEVE MOSTRAR AVISO DE SAÍDA =====
    const shouldShowExitWarning = (): boolean => {
        if (mode !== 'focus') return false;
        return focusTimeRemaining > 0;
    };

    // ===== MARCAR FOCO COMO COMPLETO =====
    const markFocusComplete = () => {
        setFocusSessionValid(true);
        console.log('✅ Sessão de foco marcada como válida');
    };

    // ===== HANDLE EXIT (COM MODAL DE CONFIRMAÇÃO) =====
    const handleExit = (callback: () => void) => {
        if (shouldShowExitWarning()) {
            // Mostrar modal de confirmação customizado (não window.confirm)
            // O modal será renderizado no componente StudyPage
            return; // Não executa callback ainda
        } else {
            // Saída normal (após 20 min)
            callback();
        }
    };

    // ===== CONFIRMAR SAÍDA (Chamado pelo modal) =====
    const confirmExit = (callback: () => void) => {
        setFocusSessionValid(false);
        console.log('❌ Modo foco interrompido - sessão inválida');
        callback();
    };

    const addFocusTime = useCallback(() => {
        if (focusStartTime.current !== null) {
            // Empurra o início 10 minutos para frente (equivale a adicionar 10 min restantes)
            focusStartTime.current = focusStartTime.current + 10 * 60 * 1000;
        }
    }, []);

    return {
        mode,
        isFocusMode: mode === 'focus',
        isReviewMode: mode === 'review',
        isFreeMode: mode === 'free',
        focusTimeRemaining,
        focusSessionValid,
        handleExit,
        confirmExit, // ✅ NOVO
        markFocusComplete,
        shouldShowExitWarning,
        addFocusTime,
    };
};